import React from 'react';
import { useLang } from '../context/LanguageContext';

const products = [
  {
    id: 'cream',
    anchor: '#product',
    orderAnchor: '#order',
    badge: 'Ceramide Complex 2%',
    badgeAr: 'مركب السيراميد 2%',
    nameEn: 'Face & Body Cream',
    nameAr: 'كريم الوجه والجسم',
    subtitleEn: 'Daily moisturizer for soft, hydrated skin',
    subtitleAr: 'مرطب يومي لبشرة ناعمة ورطبة طوال اليوم',
    capacity: '150 ml',
    price: 12,
    accent: '#e8e8e8',
    accentText: '#0a0a0a',
    icon: '🧴',
    benefitsEn: ['Ceramide barrier repair', 'All-day hydration', 'Non-greasy formula'],
    benefitsAr: ['ترميم حاجز البشرة', 'ترطيب طوال اليوم', 'تركيبة غير دهنية'],
    letter: 'C',
    bg: 'from-misky-black to-misky-dark',
    dotColor: 'bg-misky-silver',
  },
  {
    id: 'facewash',
    anchor: '#facewash',
    orderAnchor: '#order',
    badge: 'Tea Tree & Chamomile',
    badgeAr: 'شجرة الشاي والبابونج',
    nameEn: 'Tea Tree Face Wash',
    nameAr: 'غسول الوجه',
    subtitleEn: 'Gentle daily cleanser that purifies & refreshes',
    subtitleAr: 'غسول لطيف يومي ينقّي البشرة ويمنحها الانتعاش',
    capacity: '160 ml',
    price: 10,
    accent: '#166534',
    accentText: '#ffffff',
    icon: '🌿',
    benefitsEn: ['Removes impurities & oils', 'Soothes & refreshes', 'Salicylic acid formula'],
    benefitsAr: ['يزيل الشوائب والزيوت', 'يهدئ وينعش البشرة', 'تركيبة بحمض الساليسيليك'],
    letter: 'F',
    bg: 'from-[#052e16] to-[#14532d]',
    dotColor: 'bg-green-400',
  },
];

export default function ProductsOverview() {
  const { t, lang } = useLang();
  const po = t.productsOverview;
  const isAr = lang === 'ar';
  const currency = isAr ? 'دينار' : 'JD';

  return (
    <section id="products" className="py-24 bg-misky-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-misky-mid text-xs uppercase tracking-widest mb-3">{po.sectionLabel}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-misky-black">{po.title}</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {products.map((p) => (
            <div
              key={p.id}
              className={`relative bg-gradient-to-br ${p.bg} rounded-3xl overflow-hidden shadow-xl group`}
            >
              {/* Top badge */}
              <div className="absolute top-5 start-5 z-10">
                <span className="text-[10px] uppercase tracking-widest bg-white/10 text-white/80 border border-white/20 px-3 py-1 rounded-full">
                  {isAr ? p.badgeAr : p.badge}
                </span>
              </div>

              {/* Card body */}
              <div className="p-8 pt-16">
                {/* Visual placeholder */}
                <div className="flex items-center justify-center mb-8">
                  <div className="relative w-32 h-32 rounded-3xl bg-white/10 border border-white/20 flex items-center justify-center shadow-inner group-hover:scale-105 transition-transform duration-500">
                    <span className="text-5xl">{p.icon}</span>
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white/20 border border-white/30 rounded-full px-3 py-0.5 whitespace-nowrap">
                      <span className="font-medium text-white text-xs">{p.capacity}</span>
                    </div>
                  </div>
                </div>

                {/* Name */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-1">
                    {isAr ? p.nameAr : p.nameEn}
                  </h3>
                  <p className="text-white/60 text-sm">{isAr ? p.subtitleAr : p.subtitleEn}</p>
                </div>

                {/* Benefits */}
                <ul className="space-y-2 mb-8">
                  {(isAr ? p.benefitsAr : p.benefitsEn).map((b, i) => (
                    <li key={i} className="flex items-center gap-2 text-white/80 text-sm">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${p.dotColor}`} />
                      {b}
                    </li>
                  ))}
                </ul>

                {/* Price + CTA */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-3xl font-bold text-white">
                      {p.price}
                      <span className="text-sm font-normal text-white/60 ms-1">{currency}</span>
                    </div>
                    <div className="text-white/40 text-xs mt-0.5">{p.capacity}</div>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={p.anchor}
                      className="px-4 py-2.5 rounded-xl border border-white/30 text-white/80 text-sm hover:bg-white/10 transition-colors"
                    >
                      {po.viewDetails}
                    </a>
                    <a
                      href={p.orderAnchor}
                      onClick={() => {
                        if (typeof window !== 'undefined') {
                          window.__miskySelectedProduct = p.id;
                          window.dispatchEvent(new CustomEvent('misky-select-product', { detail: p.id }));
                        }
                      }}
                      className="px-4 py-2.5 rounded-xl bg-white text-misky-black text-sm font-bold hover:bg-misky-silver transition-colors"
                    >
                      {po.orderNow}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
